import { UserRole } from 'src/app/features/admin/types/user.type';
import { usableIcons } from '../icon/icon.component';

export interface NavigationItem {
  id: string;
  label: string;
  route?: string;
  icon?: usableIcons;
  color?: 'cyan' | 'yellow' | 'red';
  style?: 'underlined' | 'filled';
  requiresAuth?: boolean;
  // Opposite of requiresAuth: only shown to logged-out visitors (e.g. Login).
  hideIfAuth?: boolean;
  requiresRole?: UserRole[];
  mobileOnly?: boolean;
  desktopOnly?: boolean;
  onClick?: () => void;
}

export interface NavigationSubmenu {
  id: string;
  label: string;
  items: NavigationSubmenuItem[];
  requiresAuth?: boolean;
  requiresRole?: UserRole[];
}

export interface NavigationSubmenuItem {
  id: string;
  label: string;
  route: string;
  icon?: usableIcons;
  requiresRole?: UserRole[];
}

// Navigation configuration
export const PRIMARY_NAVIGATION: NavigationItem[] = [
  {
    id: 'finder',
    label: 'SUCHE',
    route: '/finder',
    icon: 'angleRight',
    style: 'filled',
    color: 'cyan',
  },
];

export const SECONDARY_NAVIGATION: NavigationItem[] = [
  {
    id: 'about-us',
    label: 'COMPONENTS.NAVIGATION_HEADER.ABOUT_US',
    route: '/ueber-uns',
    color: 'yellow',
    style: 'underlined',
  },
  {
    id: 'press',
    label: 'COMPONENTS.NAVIGATION_HEADER.PRESS',
    route: '/presse',
    color: 'cyan',
    style: 'underlined',
  },
  {
    id: 'donation',
    label: 'COMPONENTS.NAVIGATION_HEADER.DONATION',
    route: '/donation',
    color: 'red',
    style: 'underlined',
  },
  {
    id: 'admin',
    label: 'Admin',
    route: '/admin/dashboard',
    icon: 'admin',
    color: 'cyan',
    style: 'underlined',
    requiresAuth: true,
    requiresRole: [
      UserRole.Admin,
      UserRole.ContentCreator,
      UserRole.Counsellor,
    ],
  },
  {
    id: 'language',
    label: 'Sprachen',
    icon: 'language',
    color: 'cyan',
    style: 'underlined',
  },
  {
    id: 'login',
    label: 'COMPONENTS.NAVIGATION_HEADER.LOGIN',
    route: '/auth/login',
    icon: 'login',
    color: 'cyan',
    style: 'underlined',
    hideIfAuth: true,
  },
  {
    id: 'logout',
    label: 'Abmelden',
    icon: 'logout',
    color: 'cyan',
    requiresAuth: true,
  },
];

export const MAIN_NAVIGATION: (NavigationItem | NavigationSubmenu)[] = [
  {
    id: 'my-page',
    label: 'Meine Seite',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        route: 'user/dashboard',
        icon: 'results',
      },
      {
        id: 'offers',
        label: 'Individuelle Angebote',
        route: 'user/offers',
        icon: 'handHoldingHeart',
      },
      {
        id: 'todos',
        label: 'Meine To-Dos',
        route: 'user/progress-plan/todos',
        icon: 'list',
      },
      {
        id: 'focus',
        label: 'Mein Fokus',
        route: 'user/progress-plan/focus',
        icon: 'bullsEyeArrow',
      },
      {
        id: 'calendar',
        label: 'Meine Events',
        route: 'user/calendar',
        icon: 'calendarStar',
      },
      {
        id: 'videos',
        label: 'Video Tutorials',
        route: 'user/videos',
        icon: 'display',
      },
      {
        id: 'profile',
        label: 'Persönliche Daten',
        route: 'user/profile',
        icon: 'profileCard',
      },
      {
        id: 'adviser',
        label: 'Ansprechpartnerinnen',
        route: 'user/adviser',
        icon: 'commentQuestion',
      },
    ],
    requiresAuth: true,
  },
  {
    id: 'app',
    label: 'COMPONENTS.NAVIGATION_HEADER.APP',
    route: '/app',
    color: 'cyan',
    style: 'underlined',
  },
  {
    id: 'partner',
    label: 'COMPONENTS.NAVIGATION_HEADER.PARTNER',
    route: '/partner',
    color: 'yellow',
    style: 'underlined',
  },
  {
    id: 'voluntary-work',
    label: 'COMPONENTS.NAVIGATION_HEADER.VOLUNTEERING',
    route: '/voluntary-work',
    color: 'yellow',
    style: 'underlined',
  },
  {
    id: 'community',
    label: 'Events',
    route: '/community',
    color: 'yellow',
    style: 'underlined',
  },
];

export const MOBILE_ONLY_NAVIGATION: NavigationItem[] = [
  {
    id: 'about-us-mobile',
    label: 'COMPONENTS.NAVIGATION_HEADER.ABOUT_US',
    route: '/ueber-uns',
    color: 'yellow',
    style: 'underlined',
    mobileOnly: true,
  },
  {
    id: 'press-mobile',
    label: 'COMPONENTS.NAVIGATION_HEADER.PRESS',
    route: '/presse',
    color: 'cyan',
    style: 'underlined',
    mobileOnly: true,
  },
  {
    id: 'donation-mobile',
    label: 'COMPONENTS.NAVIGATION_HEADER.DONATION',
    route: '/donation',
    color: 'red',
    style: 'underlined',
    mobileOnly: true,
  },
  {
    id: 'admin-mobile',
    label: 'Admin',
    route: '/admin/dashboard',
    icon: 'admin',
    color: 'cyan',
    style: 'underlined',
    mobileOnly: true,
    requiresRole: [
      UserRole.Admin,
      UserRole.ContentCreator,
      UserRole.Counsellor,
    ],
  },
  {
    id: 'language-mobile',
    label: 'Sprachen',
    icon: 'language',
    color: 'cyan',
    style: 'underlined',
    mobileOnly: true,
  },
  {
    id: 'login-mobile',
    label: 'COMPONENTS.NAVIGATION_HEADER.LOGIN',
    route: '/auth/login',
    icon: 'login',
    color: 'cyan',
    style: 'underlined',
    mobileOnly: true,
    hideIfAuth: true,
  },
  {
    id: 'logout-mobile',
    label: 'Abmelden',
    icon: 'logout',
    color: 'cyan',
    mobileOnly: true,
    requiresAuth: true,
  },
];
